<template>
  <div v-if="applicationDetails" :key="applicationDetails.id" class="w-full">
    <ShowApplicationSkeleton v-if="isLoading" />
    <div v-else class="w-full">
      <a
        :href="sitterProfileURL"
        target="_blank"
        :aria-label="`Open ${sitter.displayName}'s profile in a new tab`"
        class="flex flex-row pr-28 md:sticky z-[9] bg-transparent border-b-1 border-grey-divider-subdued border-solid w-full md:bg-white md-1:top-80"
      >
        <div class="flex flex-col w-full p-16">
          <div class="flex flex-row items-center">
            <button class="inline-block md:hidden" @click.stop.prevent="showPhoto = true">
              <Avatar
                class="align-middle mb-8"
                :image-url="sitter.photoUrl"
                :size="AvatarSize.LARGE"
                :name="sitter.displayName + ' avatar'"
              />
            </button>
            <h1 class="desktop-ellipsis align-middle text-h2 md:mt-8 md:mb-24 pl-8 md:pl-0 md:ml-0 w-full">
              {{ sitter.displayName }}
            </h1>
          </div>
          <p class="text-body desktop-ellipsis">
            Applied to your job
            <router-link :to="jobListLink" class="text-grey-clickable underline" @click.prevent>{{
              applicationDetails?.job?.title
            }}</router-link>
          </p>
        </div>
        <div class="flex items-center absolute top-0 right-0 md:static">
          <button class="hidden md:block" @click.prevent="showPhoto = true">
            <Avatar :image-url="sitter.photoUrl" :size="AvatarSize.MEGA" :name="sitter.displayName + ' avatar'" class="mr-28" />
          </button>
          <Button id="modal-toggle" variant="outlined" class="border-0 p-24" @click.prevent="showPopup = true">
            <MoreIcon color="#4B4C52" />
          </Button>
        </div>
      </a>
      <Popup
        v-model="showPhoto"
        :popup-header="sitter.displayName + (sitter.photoUrl ? `'s profile photo` : ` doesn't have a profile photo`)"
      >
        <img v-if="sitter.photoUrl" :src="sitter.photoUrl" class="w-full" />
        <Button v-else variant="primary" class="w-full mt-1" value="OK" @click="showPhoto = false" />
      </Popup>
      <Popup v-model="showPopup" popup-header="Manage application">
        <Button
          v-if="sitter?.state == 'active'"
          variant="outlined"
          value="view full profile"
          :to="sitterProfileURL"
          class="w-full mb-16"
        />
        <Button variant="outlined" :value="'Report ' + sitter.displayName" :href="reportURL" class="w-full" />
      </Popup>
      <div class="bg-grey-background-subdued">
        <div v-if="sitter?.state !== 'active'" class="flex flex-col">
          <AlertBanner :status="3" class="w-full">
            {{ alertBannerText }}
          </AlertBanner>
          <ReplyContainer v-if="!isNotAFit" class="lg:w-1/3 self-end mb-80 p-16" />
        </div>

        <div v-else class="flex flex-col lg:flex-row pt-16 pb-80 md:pb-8">
          <div class="w-full lg:w-2/3 px-16 pb-5">
            <div v-if="sitter?.state === 'active'">
              <div>
                <Card v-show="!isQuickApply" class="w-full mb-24">
                  <h3 class="text-h4 mb-8">From {{ sitter.displayName }}</h3>
                  <ViewMoreTextBox view-more-button-text="View full message" text-box-classes="text-grey-body">{{
                    messageCardText
                  }}</ViewMoreTextBox>
                </Card>
                <Card v-if="showMatchSection" class="w-full mb-24">
                  <ApplicantMatchScore
                    v-if="isApplicantMatchScoreEnabled"
                    :application-details="applicationDetails"
                    :is-basic="isBasic"
                  />
                  <ApplicantMatch v-else :application-details="applicationDetails" :is-basic="isBasic" />
                </Card>
                <Card class="w-full mb-24">
                  <h3 class="text-h4 mb-16">Trust & Safety</h3>
                  <BackgroundCheckOverview :badges="sitter.badges" :idv="sitter.idv" :sitter-id="sitter.id" />
                </Card>
                <Card class="w-full mb-24">
                  <h3 class="text-h4 mb-16">Schedule</h3>
                  <GeneralAvailability
                    v-if="showGeneralAvailability"
                    :schedule="generalAvailability"
                    :sitter-name="sitter.displayName"
                    :availability-valid-until="availabilityValidUntil"
                    :care-type="applicantCareType"
                    :edit-mode-active="false"
                    class="border-t-1 border-solid border-grey-divider-subdued pt-16"
                  />
                  <AvailabilityCalendar
                    :account-id="sitter.id"
                    :edit-mode-active="false"
                    always-mobile-variant
                    :class="hasCalendarAvailability ? 'border-t-1 border-solid border-grey-divider-subdued pt-16 mt-16' : ''"
                    @update-calendar-availability="updateCalendarAvailability"
                  />
                </Card>
                <Card v-if="sitter.reviews.edges.length" class="w-full mb-24">
                  <RatingsAndReviews v-model="reviewsDisplayedCount" />
                </Card>
                <Button value="View full profile" size="medium" :to="sitterProfileURL" class="w-full mb-16" />
              </div>
              <div v-if="applicantIsDismissed" class="md:hidden -mt-20">
                <div
                  class="w-full bottom-0 left-0 border-t-1 border-solid border-grey-divider-subdued bg-foreground mt-16 mb-16"
                />
                <HiringTip />
              </div>
            </div>
          </div>
          <ReplyContainer class="w-full lg:w-1/3 mb-[140px] pr-16" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-nocheck

// Components
import AlertBanner from '../../components/alert_banner/AlertBanner.vue';
import Avatar, { AvatarSize } from '../../components/avatar/Avatar.vue';
import Button from '../../components/button/Button.vue';
import Card from '../../components/card/Card.vue';
import Popup from '../../components/popup/Popup.vue';
import ReplyContainer from './ReplyContainer/ReplyContainer.vue';
import RatingsAndReviews from './RatingsAndReviews.vue';
import ShowApplicationSkeleton from './ShowApplicationSkeleton.vue';
import ApplicantMatch from './ApplicantMatch.vue';
import ApplicantMatchScore from './ApplicantMatchScore.vue';
import GeneralAvailability from '@/components/general_availability/GeneralAvailability.vue';
import AvailabilityCalendar from '@/components/calendar/AvailabilityCalendar.vue';
import BackgroundCheckOverview from '@/components/background_check_overview/BackgroundCheckOverview.vue';

// Icons
import MoreIcon from '@/graphics/icons/MoreIcon.vue';
import ViewMoreTextBox from '@/components/view_more_text_box/ViewMoreTextBox.vue';
import HiringTip from './ReplyContainer/HiringTip.vue';

// Lib
import { FETCH_APPLICATION_DETAILS } from '@/graphql/applications/queries';
import getProviderProfileUrl from '@/helpers/providerProfileUrl';
import { isOn } from '@/lib/growthbook';

const defaultReviewsDisplayedCount: number = 3;

export default {
  name: 'ShowApplication',
  components: {
    AlertBanner,
    Avatar,
    Button,
    Card,
    Popup,
    ReplyContainer,
    RatingsAndReviews,
    ShowApplicationSkeleton,
    ApplicantMatch,
    ApplicantMatchScore,
    MoreIcon,
    ViewMoreTextBox,
    HiringTip,
    GeneralAvailability,
    AvailabilityCalendar,
    BackgroundCheckOverview,
  },
  data() {
    return {
      AvatarSize,
      showPopup: false,
      showPhoto: false,
      reviewsDisplayedCount: defaultReviewsDisplayedCount,
      hasCalendarAvailability: true,
    };
  },
  computed: {
    applicantIsDismissed(): boolean {
      const { parentInterestStatus } = this.$store.getters['applications/applicationDetails'];
      return parentInterestStatus !== 'NOT_A_FIT' && parentInterestStatus !== 'INTERESTED';
    },
    applicationDetails(): any {
      return this.$store.getters['applications/applicationDetails'] || {};
    },
    sitter(): any {
      return this.applicationDetails?.sitter || {};
    },
    isNotAFit(): boolean {
      return this.$store.getters['applications/applicationDetails'].parentInterestStatus === 'NOT_A_FIT';
    },
    isLoading(): boolean {
      return this.$store.getters['applications/showApplicationLoading'];
    },
    isBasic(): boolean {
      return this.$store.getters['account/getAccount']?.isBasic() || false;
    },
    isChildcareJob(): boolean {
      const careType = this.applicationDetails?.job?.careType;
      return careType === 'BABYSITTER' || careType === 'NANNY';
    },
    reportURL(): string {
      if (!this.applicationDetails) return '';

      const isStaging: boolean = window.location.href.includes('staging.sittercity.com');
      const domainURL: string = `https://admin${isStaging ? '-staging' : ''}.sittercity.com`;

      const adminLink: string = `${domainURL}/admins/accounts/${this.sitter.id}/messages/${this.applicationDetails.memberCommunicationId}`;
      return `https://support.sittercity.com/hc/en-us/requests/new?ticket_form_id=360000078194&related_url=${adminLink}&sitter_uuid=${this.sitter.id}`;
    },
    isQuickApply(): boolean {
      return this.applicationDetails?.reviewedApplicationText?.includes('This is an automated message from Sittercity.') || false;
    },
    messageCardText(): string {
      return (
        this.applicationDetails.reviewedApplicationText ||
        this.sitter.careTypeDetails[this.$store.getters['applications/applicantCareTypeKey']].bio ||
        ''
      );
    },
    sitterProfileURL(): string {
      const careTypeKey = this.$store.getters['applications/applicantCareTypeKey'];
      return getProviderProfileUrl(this.sitter?.id, careTypeKey, this.sitter?.providerType) || '';
    },
    jobListLink(): { name: string } {
      const job = this.applicationDetails?.job;
      if (!job) return { name: 'seeker-jobs-open' };
      const isOpen = job.isActive || job.status === 'PENDING';
      return { name: isOpen ? 'seeker-jobs-open' : 'seeker-jobs-closed' };
    },
    alertBannerText(): String {
      if (this.sitter?.state === 'closed') {
        return "This user can't be contacted because their Sittercity account is inactive.";
      }
      if (this.sitter?.state === 'terminated' || this.sitter?.state === 'scammer') {
        return 'This user has been removed from the Sittercity network. Check your email for additional information.';
      }
      return 'This user is no longer a member in the Sittercity network.';
    },
    applicantCareType(): string {
      return this.$store.getters['applications/applicantCareTypeKey'] || '';
    },
    generalAvailability() {
      return this.sitter?.profiles?.[this.applicantCareType]?.profile?.generalAvailability || [];
    },
    availabilityValidUntil(): string | null {
      return this.sitter?.profiles?.[this.applicantCareType]?.profile?.availabilityValidUntil || null;
    },
    showGeneralAvailability(): boolean {
      return this.generalAvailability.length > 0 || !this.hasCalendarAvailability;
    },
    isApplicantMatchScoreEnabled(): boolean {
      return isOn('sc-applications-match-score');
    },
    showMatchSection(): boolean {
      const newMatchRequirements = (this.isChildcareJob && !!this.applicationDetails.matchSummary) || this.isBasic;
      const oldMatchRequirements = this.applicationDetails?.matchingRequirements?.length > 0 || this.isBasic;
      if (this.isApplicantMatchScoreEnabled) {
        return newMatchRequirements;
      }
      return oldMatchRequirements;
    },
  },
  watch: {
    $route(): void {
      // this watcher can be used to reset data values when switching applicants
      this.reviewsDisplayedCount = defaultReviewsDisplayedCount;
      this.$store.dispatch('applications/showApplicationLoading', true);
      this.fetchApplicationDetails();
    },
    reviewsDisplayedCount(): void {
      this.fetchApplicationDetails();
    },
  },
  created() {
    this.fetchApplicationDetails();
  },
  methods: {
    async fetchApplicationDetails() {
      this.$store.dispatch('applications/showApplicationLoading', true);

      try {
        const { data } = await this.$apollo.query({
          query: FETCH_APPLICATION_DETAILS,
          fetchPolicy: 'network-only',
          variables: {
            application_uuid: this.$route?.params?.application_uuid,
            reviewsFetchCount: this.reviewsDisplayedCount,
          },
        });
        this.$store.commit('applications/setApplicationDetails', data.applicationDetails);
      } catch (error) {
        console.error('GraphQL query error:', error);
      } finally {
        this.$store.dispatch('applications/showApplicationLoading', false);
      }
    },
    updateCalendarAvailability(val: boolean): void {
      this.hasCalendarAvailability = val;
    },
  },
};
</script>

<style scoped>
@media (min-width: 768px) {
  .desktop-ellipsis {
    overflow: hidden;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    -webkit-line-clamp: 1;
  }
}
</style>
